/**
 * Geode Voice (UPT-2000 lot V0 §5.4) — appel NON INTERACTIF du flux du
 * copilote : mêmes en-têtes et même corps que le tiroir (`buildStreamBody`,
 * contexte de page courant), lecture du flux SSE jusqu'à la fin.
 *
 * Toute demande d'écriture (`write_confirmation`) est IGNORÉE : pas de
 * confirmation vocale en V0 — la réponse devient « Cette action demande une
 * confirmation à l'écran ». Le texte final est tronqué à 1 200 caractères.
 * Les valeurs masquées `••••` restent masquées (le serveur caviarde déjà).
 */
import { buildStreamBody } from '@/uptk/core/components/copilot/copilotWriteConfirmation';
import { voiceFetchHeaders } from './voiceApi';

export const ASK_COPILOT_MAX_LENGTH = 1_200;

export type AskCopilotResult = {
  text: string;
  /** Vrai quand le copilote a demandé une écriture (refusée à la voix). */
  writeRequested: boolean;
};

export const WRITE_CONFIRMATION_REFUSAL =
  "Cette action demande une confirmation à l'écran";

type SseBlock = { event: string; data: string };

const parseSseBlocks = (buffer: string): { blocks: SseBlock[]; rest: string } => {
  const blocks: SseBlock[] = [];
  let rest = buffer;
  for (;;) {
    const end = rest.indexOf('\n\n');
    if (end < 0) {
      break;
    }
    const raw = rest.slice(0, end);
    rest = rest.slice(end + 2);
    let event = '';
    const dataLines: string[] = [];
    raw.split('\n').forEach((line) => {
      if (line.startsWith('event:')) {
        event = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    });
    if (event) {
      blocks.push({ event, data: dataLines.join('\n') });
    }
  }
  return { blocks, rest };
};

/**
 * Pose `question` au copilote et renvoie le texte final (tronqué), ou le
 * refus de confirmation si une écriture était demandée.
 */
export const askCopilotOnce = async (
  question: string,
  pageContext: string,
  signal?: AbortSignal
): Promise<AskCopilotResult> => {
  // En-têtes partagés avec les rapports d'usage (V1a) : `voiceFetchHeaders` est l'ancien `buildHeaders` extrait.
  const headers = await voiceFetchHeaders();
  const response = await fetch('/api/v1/ai/copilot/stream', {
    method: 'POST',
    headers,
    signal: signal ?? null,
    body: JSON.stringify(
      buildStreamBody({
        message: question,
        conversationId: null,
        pageContext,
      })
    ),
  });
  if (!response.ok || !response.body) {
    throw new Error(`copilot stream: statut ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';
  let writeRequested = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const { blocks, rest } = parseSseBlocks(buffer);
    buffer = rest;
    for (const block of blocks) {
      if (block.event === 'message') {
        answer = block.data;
      } else if (block.event === 'write_confirmation') {
        writeRequested = true;
      } else if (block.event === 'error') {
        throw new Error('copilot stream: erreur serveur');
      }
    }
  }

  if (writeRequested) {
    return { text: WRITE_CONFIRMATION_REFUSAL, writeRequested: true };
  }
  const trimmed = answer.trim();
  return {
    text: trimmed.length > ASK_COPILOT_MAX_LENGTH ? trimmed.slice(0, ASK_COPILOT_MAX_LENGTH) : trimmed,
    writeRequested: false,
  };
};
